import { subsystemShortName } from '../deployment/subsystems';
import type { SubsystemId} from '../deployment/subsystemData';
import { subsystems } from '../deployment/subsystemData';
import type { Logger } from '../types/logger';
import { RED, RESET } from './colors';
import type { LogAggregator, LogEntry } from '@mimir/primitive/structuredLogger';
import { writeDecoratedEntryToConsole, writeLogEntryToConsole } from '../log/consoleSLogger';
import { assert } from './invariant';

interface LogTarget {
  write(entry: LogEntry): void;
  flush(): Promise<void>;
}

interface LogOptions {
  emitDebug: boolean;
  emitSilly: boolean;
  target: LogTarget
}

export class BaseLogger implements Logger {
  constructor(
    private options: LogOptions,
    private annotationInfo: Record<string, unknown>,
    private mutable: boolean,
    private aggregators: Record<string, LogAggregator>
  ) {
    assert(!('scope' in annotationInfo) || annotationInfo.scope !== undefined, 'Scope in annotation info must not be set undefined value, since it must be representable in JSON');
  }

  setSillyMode(enabled: boolean) {
    if (this.mutable) {
      this.options.emitSilly = enabled;
    }
  }

  sillyModeEnabled() {
    return this.options.emitSilly;
  }

  setDebugMode(enabled: boolean) {
    if (this.mutable) {
      this.options.emitDebug = enabled;
    }
  }

  debugModeEnabled() {
    return this.options.emitDebug;
  }

  silly(message: string | (() => string)): void {
    if (this.options.emitSilly) {
      const msg = (typeof message === 'function') ? message() : message;
      this.log({ message: msg, level: 'debug' });
    }
  }

  debug(message: string): void {
    if (this.options.emitDebug || this.options.emitSilly) {
      this.log({ message, level: 'debug' });
    }
  }

  info(message: string): void {
    this.log({ message, level: 'info' });
  }

  warn(message: string): void {
    this.log({ message, level: 'warn' });
  }

  error(message: string): void {
    this.log({ message, level: 'error' });
  }

  log(entry: LogEntry) {
    assert(!('scope' in entry) || entry.scope !== undefined, 'Scope in log entry must not be set undefined value, since it must be representable in JSON');
    if (entry.level === 'debug' && !this.options.emitDebug) {
      return;
    }
    const full = { ...this.annotationInfo, ...entry };
    for (const aggregator of Object.values(this.aggregators)) {
      if (aggregator.tryAdd(full)) {
        return;
      }
    }

    this.options.target.write(full);
  }

  async flush(): Promise<void> {
    for (const aggregator of Object.values(this.aggregators)) {
      const summary = await aggregator.flush();
      if (summary) {
        this.options.target.write({ ...this.annotationInfo, ...summary });
      }
    }
    await this.options.target.flush();
  }

  close() {
    return this.flush();
  }

  child(info: Record<string, unknown>, aggregators?: Record<string, LogAggregator>): Logger {
    let scope = this.annotationInfo.scope as string | undefined;
    if (scope) {
      if (info.scope) {
        scope = scope + '/' + String(info.scope);
      }
    } else {
      scope = info.scope as string | undefined;
    }

    const annotationInfo = { ...this.annotationInfo, ...info };
    if (scope) {
      annotationInfo.scope = scope;
    }
    return new BaseLogger(
      this.options,
      annotationInfo,
      false,
      aggregators ? { ...this.aggregators, ...aggregators } : this.aggregators
    );
  }
}

export class NoLogger extends BaseLogger {
  constructor() {
    super(
      {
        emitDebug: false,
        emitSilly: false,
        target: {
          write() {
            // Write nothing
          },
          async flush() {
            // Flush nothing
          }
        }
      },
      {},
      false,
      {}
    );
  }
}

export const NO_LOGGER = new NoLogger();

export class ConsoleLogger extends BaseLogger {
  constructor(info?: Record<string, unknown>) {
    super(
      {
        emitDebug: false,
        emitSilly: false,
        target: {
          write(entry: LogEntry) {
            writeLogEntryToConsole(entry);
          },
          async flush() {
            // No need to flush console
          }
        }
      },
      info ?? {},
      true,
      {}
    );
  }
}

export class QuietLogger extends BaseLogger {
  constructor() {
    super(
      {
        emitDebug: true,
        emitSilly: true,
        target: {
          write(entry: LogEntry) {
            if (entry.level === 'error') {
              writeLogEntryToConsole(entry);
            }
          },
          async flush() {
            // No need to flush console
          }
        }
      },
      {},
      false,
      {}
    );
  }
}

export class DecoratedConsoleLogger extends BaseLogger {

  constructor(pfx: string, colorErrorsInRed = true) {
    const errorColor = colorErrorsInRed ? RED : '';
    let N = 0;
    super(
      {
        emitDebug: false,
        emitSilly: false,
        target: {
          write(entry: LogEntry) {
            writeDecoratedEntryToConsole(pfx, errorColor, N++, entry);
          },
          async flush() {
            // No need to flush console
          }
        }
      },
      {},
      true,
      {}
    );
  }
}

/**
 * Consider using getContextLogger() instead of this function for a more
 * integrated logging experience, which automatically aggregates milestone
 * timing and consumption on a per-request basis.
 */
export function getLambdaLogger(subsystemId: SubsystemId, functionName: string): Logger {
  /*
    This "typeof process" guard used to be neccessary to avoid pulling in a
    process polyfill in browser builds, before we found the polyfill settings to
    suppress pulling in the process global.

    Now the guard only serves as a reminder that this is an unclean module that
    contains both function that are intended for browsers and functions that
    won't work properly in browsers.

    TODO: Move getLambdaLogger() to packages/server-common where unguarded
    access to process is acceptable.
  */
  if (typeof process !== 'undefined') {
    if (process.env.SERVE2 === undefined) {
      return new ConsoleLogger();
    }
  }
  const subsystem = subsystems[subsystemId];
  const prefix = `${subsystem.color}[${subsystemShortName(subsystemId)}:${functionName}]${RESET} `;
  return new DecoratedConsoleLogger(prefix);
}
